/**
 * FeedbackDashboard Component
 *
 * Kanban-style dashboard for managing feature requests with drag-and-drop.
 * Admin-only component.
 *
 * @module components/dev/FeedbackDashboard
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Badge,
  Stack,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Assessment as AnalyticsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useUser } from '../../contexts/AuthContext';
import { FeatureRequestService } from '../../services/featureRequestService';
import RequestCard from './RequestCard';
import KanbanColumn from './KanbanColumn';
import {
  FeatureRequest,
  RequestStatus,
  RequestType,
  STATUS_BADGES,
  REQUEST_TYPES,
} from '../../types/featureRequest';

/**
 * FeedbackDashboard - Kanban board for managing requests
 */
const FeedbackDashboard: React.FC = () => {
  const user = useUser();
  const navigate = useNavigate();

  // State
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<RequestType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Columns configuration
  const columns: RequestStatus[] = [
    'new',
    'under-review',
    'in-progress',
    'completed',
    'wont-do',
    'needs-info',
  ];

  /**
   * Fetch all requests
   */
  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const allRequests = await FeatureRequestService.getAllRequests();
      setRequests(allRequests);
    } catch (err: any) {
      console.error('Error fetching requests:', err);
      setError(err.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle drag start
   */
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  /**
   * Handle drag end - update request status
   */
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const requestId = active.id as string;
    const newStatus = over.id as RequestStatus;

    try {
      // Optimistically update UI
      setRequests((prevRequests) =>
        prevRequests.map((req) =>
          req.id === requestId ? { ...req, status: newStatus, updatedAt: new Date().toISOString() } : req
        )
      );

      // Update in database
      await FeatureRequestService.updateRequestStatus(
        requestId,
        newStatus,
        user?.uid || 'admin',
        user?.displayName || 'Admin'
      );
    } catch (err: any) {
      console.error('Error updating request status:', err);
      setError('Failed to update request status');
      // Revert on error
      await fetchRequests();
    }
  };

  /**
   * Filter requests
   */
  const getFilteredRequests = (): FeatureRequest[] => {
    let filtered = [...requests];

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter((req) => req.type === filterType);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (req) =>
          req.title.toLowerCase().includes(query) ||
          req.description.toLowerCase().includes(query) ||
          req.submittedByName?.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  /**
   * Get requests by status
   */
  const getRequestsByStatus = (status: RequestStatus): FeatureRequest[] => {
    return getFilteredRequests().filter((req) => req.status === status);
  };

  /**
   * Get count by status
   */
  const getCountByStatus = (status: RequestStatus): number => {
    return getRequestsByStatus(status).length;
  };

  /**
   * Get active request
   */
  const getActiveRequest = (): FeatureRequest | undefined => {
    return requests.find((req) => req.id === activeId);
  };

  // Check if user is admin
  if (user && user.role !== 'admin') {
    return (
      <Box maxWidth={800} mx="auto" p={3}>
        <Alert severity="error">
          Access Denied: You must be an administrator to access this page.
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredRequests = getFilteredRequests();

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Feature Request Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and track all feature requests
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchRequests}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<AnalyticsIcon />}
            onClick={() => navigate('/dev/feedback-analytics')}
          >
            Analytics
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/feedback/submit')}
          >
            New Request
          </Button>
        </Stack>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Search"
              placeholder="Search requests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Filter by Type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as RequestType | 'all')}
              fullWidth
              size="small"
            >
              <MenuItem value="all">All Types</MenuItem>
              {Object.entries(REQUEST_TYPES).map(([value, config]) => (
                <MenuItem key={value} value={value}>
                  {config.icon} {config.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Kanban Board */}
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Grid container spacing={2}>
          {columns.map((status) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={status}>
              <KanbanColumn
                status={status}
                requests={getRequestsByStatus(status)}
                count={getCountByStatus(status)}
              />
            </Grid>
          ))}
        </Grid>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeId ? (
            <Box sx={{ opacity: 0.8 }}>
              {getActiveRequest() && <RequestCard request={getActiveRequest()!} isDragging />}
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Empty State */}
      {filteredRequests.length === 0 && !loading && (
        <Paper elevation={2} sx={{ p: 6, textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No requests found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {searchQuery || filterType !== 'all'
              ? 'Try adjusting your filters'
              : 'No feature requests have been submitted yet'}
          </Typography>
          {(searchQuery || filterType !== 'all') && (
            <Button
              onClick={() => {
                setSearchQuery('');
                setFilterType('all');
              }}
            >
              Clear Filters
            </Button>
          )}
        </Paper>
      )}

      {/* Summary Stats */}
      <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Summary
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {columns.map((status) => (
            <Chip
              key={status}
              label={`${STATUS_BADGES[status].icon} ${STATUS_BADGES[status].label}: ${getCountByStatus(status)}`}
              size="small"
              sx={{
                bgcolor: STATUS_BADGES[status].color,
                color: 'white',
              }}
            />
          ))}
        </Stack>
      </Paper>
    </Box>
  );
};

export default FeedbackDashboard;
